


















































































































































import { Component, Vue, Prop } from 'vue-property-decorator';
import AuthModule from '@/store/AuthModule';

@Component({
    filters: {
        formatDenounceStatus(status: string) {
            const map: Record<string, string> = {
                accepted: 'Acceptée',
                pending: 'En attente',
                refused: 'Refusée',
            };
            return map[status] || status;
        },
    },
})
export default class DenounceReportModal extends Vue {
    @Prop({ required: true }) denounces!: any[];

    step: string = 'confirm';
    progress: number = 0;
    progressMessage: string = '';

    progressSteps = [
        { at: 10,  label: 'Préparation des données...' },
        { at: 30,  label: 'Analyse des dénonces...' },
        { at: 55,  label: 'Construction du document...' },
        { at: 80,  label: 'Mise en forme du PDF...' },
        { at: 95,  label: 'Ouverture du rapport...' },
    ];

    get totalLots(): number {
        return this.denounces.reduce((acc, d) => acc + (d.lots ? d.lots.length : 0), 0);
    }

    get acceptedCount(): number {
        return this.denounces.filter(d => d.status === 'accepted').length;
    }

    get currentUserName(): string {
        return `${AuthModule.firstname || ''} ${AuthModule.lastname || ''}`.trim() || 'N/A';
    }

    get currentUserPhone(): string {
        return (AuthModule as any).mobile || (AuthModule as any).phone || '';
    }

    get currentUserCompany(): string {
        const c = (AuthModule as any).company;
        return c && c.name ? c.name : '';
    }

    sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async animateTo(target: number, label: string, ms: number) {
        this.progressMessage = label;
        const step = (target - this.progress) / 10;
        for (let i = 0; i < 10; i++) {
            await this.sleep(ms / 10);
            this.progress = Math.min(target, Math.round(this.progress + step));
        }
    }

    async startGeneration() {
        this.step = 'generating';
        this.progress = 0;

        await this.animateTo(10,  'Préparation des données...', 400);
        await this.animateTo(30,  'Analyse des dénonces...', 500);
        await this.animateTo(55,  'Construction du document...', 600);

        const html = this.buildPdfHtml();

        await this.animateTo(80,  'Mise en forme du PDF...', 400);
        await this.animateTo(95,  'Ouverture du rapport...', 300);

        const win = window.open('', '_blank');
        if (win) {
            win.document.write(html);
            win.document.close();
            win.addEventListener('load', function() {
                setTimeout(function() {
                    win.print();
                }, 2000);
            });
        }

        await this.animateTo(100, 'Terminé !', 200);
        await this.sleep(400);
        this.step = 'done';
    }

    formatStatus(status: string): string {
        const map: Record<string, string> = {
            accepted: 'Acceptée',
            pending:  'En attente',
            refused:  'Refusée',
        };
        return map[status] || status;
    }

    formatLotStatus(status: string): string {
        const map: Record<string, string> = {
            available: 'Disponible',
            optionned: 'Optionné',
            reserved:  'Réservé',
            booked:    'Réservé',
            acted:     'Acté',
            retired:   'Retiré',
            sold:      'Vendu',
        };
        return map[status] || status;
    }

    statusColor(status: string): string {
        const map: Record<string, string> = {
            accepted: '#22c55e',
            pending:  '#f59e0b',
            refused:  '#ef4444',
        };
        return map[status] || '#94a3b8';
    }

    statusBg(status: string): string {
        const map: Record<string, string> = {
            accepted: '#f0fdf4',
            pending:  '#fffbeb',
            refused:  '#fef2f2',
        };
        return map[status] || '#f8fafc';
    }

    lotStatusColor(status: string): string {
        const map: Record<string, string> = {
            available: '#22c55e',
            optionned: '#f59e0b',
            reserved:  '#3b82f6',
            booked:    '#3b82f6',
            acted:     '#8b5cf6',
            retired:   '#94a3b8',
            sold:      '#64748b',
        };
        return map[status] || '#94a3b8';
    }

    safeHtml(text: string): string {
        // Step 1: decode any pre-encoded HTML entities coming from the server (e.g. &#39; → ')
        const decoder = document.createElement('div');
        decoder.innerHTML = text || '';
        const decoded = decoder.textContent || '';
        // Step 2: re-encode for safe HTML output
        const encoder = document.createElement('div');
        encoder.textContent = decoded;
        return encoder.innerHTML;
    }

    buildPdfHtml(): string {
        const date = new Date().toLocaleDateString('fr-FR', {
            day: '2-digit', month: 'long', year: 'numeric',
        });
        const userName    = this.safeHtml(this.currentUserName);
        const userPhone   = this.safeHtml(this.currentUserPhone);
        const userCompany = this.safeHtml(this.currentUserCompany);

        /* ---------- build each denounce section ---------- */
        let denouncesHtml = '';
        this.denounces.forEach((d, idx) => {
            const statusLabel = this.formatStatus(d.status);
            const sColor      = this.statusColor(d.status);
            const sBg         = this.statusBg(d.status);

            /* lots table */
            let lotsHtml = '';
            if (d.lots && d.lots.length > 0) {
                const rows = d.lots.map((lot: any) => {
                    const price   = lot.price   ? Number(lot.price).toLocaleString('fr-FR')  + ' €' : '—';
                    const surface = lot.surface  ? lot.surface  + ' m²' : '—';
                    const lStatus = this.formatLotStatus(lot.status);
                    const lColor  = this.lotStatusColor(lot.status);
                    const type2   = this.safeHtml(lot.type2 || lot.type || '—');
                    const floor   = this.safeHtml(String(lot.floor || '—'));
                    const name    = this.safeHtml(lot.name  || '—');
                    const parking = [lot.parking1, lot.parking2].filter(Boolean).join(', ') || '—';
                    const extras: string[] = [];
                    if (lot.terrace)  extras.push('Terrasse ' + lot.terrace + ' m²');
                    if (lot.varangue) extras.push('Varangue ' + lot.varangue + ' m²');
                    if (lot.garden)   extras.push('Jardin ' + lot.garden + ' m²');
                    return `
                        <tr>
                            <td class="td-lot-name">${name}</td>
                            <td>${type2}</td>
                            <td class="td-center">${floor}</td>
                            <td class="td-center">${surface}</td>
                            <td class="td-right td-price">${price}</td>
                            <td class="td-center">
                                <span class="ls-badge" style="color:${lColor};border-color:${lColor};">${lStatus}</span>
                            </td>
                            <td class="td-center">${parking}</td>
                            <td class="td-center td-extras">${extras.join(' · ') || '—'}</td>
                        </tr>`;
                }).join('');

                lotsHtml = `
                    <div class="lots-wrap">
                        <div class="lots-title">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                            Lots concernés <span class="lots-count">${d.lots.length}</span>
                        </div>
                        <table class="lots-table">
                            <thead>
                                <tr>
                                    <th>Lot</th>
                                    <th>Type</th>
                                    <th class="th-center">Étage</th>
                                    <th class="th-center">Surface</th>
                                    <th class="th-right">Prix TTC</th>
                                    <th class="th-center">Statut</th>
                                    <th class="th-center">Parking</th>
                                    <th class="th-center">Annexes</th>
                                </tr>
                            </thead>
                            <tbody>${rows}</tbody>
                        </table>
                    </div>`;
            }

            const promoterLine = d.promoterName
                ? `<span class="meta-item"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>${this.safeHtml(d.promoterName)}</span>`
                : '';
            const deliveryLine = d.delivery
                ? `<span class="meta-item"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>Livraison : ${this.safeHtml(d.delivery)}</span>`
                : '';
            const addressLine = d.address
                ? `<span class="meta-item"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>${this.safeHtml(d.address)}</span>`
                : '';

            denouncesHtml += `
                <div class="denounce-card ${idx > 0 ? 'page-break-before' : ''}">
                    <div class="dc-header" style="border-left-color:${sColor};">
                        <div class="dc-header-left">
                            <div class="dc-client">
                                <span class="dc-idx">#${idx + 1}</span>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                ${this.safeHtml(d.customer)}
                            </div>
                            <div class="dc-program">${this.safeHtml(d.programName || 'Programme N° ' + d.programId)}</div>
                            <div class="dc-meta">
                                ${addressLine}
                                ${promoterLine}
                                ${deliveryLine}
                                ${d.updatedAt ? `<span class="meta-item meta-date">Màj : ${this.safeHtml(d.updatedAt)}</span>` : ''}
                            </div>
                        </div>
                        <div class="dc-header-right">
                            <span class="status-pill" style="color:${sColor};background:${sBg};border-color:${sColor};">
                                ${statusLabel}
                            </span>
                            <span class="lots-pill">${d.lots ? d.lots.length : 0} lot(s)</span>
                        </div>
                    </div>
                    ${lotsHtml}
                </div>`;
        });

        /* ---------- full HTML ---------- */
        return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Rapport Dénonces — ${date}</title>
<style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    @page {
        size: A4 landscape;
        margin: 14mm 16mm 14mm 16mm;
    }

    body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
        font-size: 10px;
        color: #1e293b;
        background: #fff;
        line-height: 1.5;
    }

    /* ── Cover-style header ─────────────────────── */
    .pdf-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding-bottom: 14px;
        border-bottom: 3px solid #1e40af;
        margin-bottom: 18px;
    }
    .pdf-header-left {}
    .pdf-title {
        font-size: 22px;
        font-weight: 800;
        color: #1e40af;
        letter-spacing: -0.5px;
        line-height: 1.1;
    }
    .pdf-subtitle {
        font-size: 11px;
        color: #64748b;
        margin-top: 3px;
    }
    .pdf-header-right {
        text-align: right;
        font-size: 9.5px;
        color: #475569;
        line-height: 1.7;
    }
    .pdf-header-right strong {
        display: block;
        font-size: 11px;
        color: #1e293b;
        font-weight: 700;
    }
    .pdf-date {
        display: inline-block;
        margin-top: 4px;
        padding: 2px 8px;
        background: #f1f5f9;
        border-radius: 20px;
        font-size: 9px;
        color: #64748b;
    }

    /* ── Summary bar ────────────────────────────── */
    .summary-bar {
        display: flex;
        gap: 0;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        overflow: hidden;
        margin-bottom: 20px;
    }
    .summary-item {
        flex: 1;
        text-align: center;
        padding: 10px 6px;
        border-right: 1px solid #e2e8f0;
    }
    .summary-item:last-child { border-right: none; }
    .summary-val {
        display: block;
        font-size: 20px;
        font-weight: 800;
        color: #1e40af;
        line-height: 1;
    }
    .summary-lbl {
        display: block;
        font-size: 8.5px;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-top: 3px;
    }

    /* ── Denounce card ──────────────────────────── */
    .denounce-card {
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        overflow: hidden;
        margin-bottom: 16px;
        page-break-inside: avoid;
    }
    .page-break-before { page-break-before: auto; }

    .dc-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: 12px 16px;
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        border-left: 4px solid #1e40af;
        border-bottom: 1px solid #e2e8f0;
    }
    .dc-client {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        font-weight: 700;
        color: #0f172a;
        margin-bottom: 3px;
    }
    .dc-idx {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #1e40af;
        color: #fff;
        font-size: 9px;
        font-weight: 800;
    }
    .dc-program {
        font-size: 11px;
        color: #1e40af;
        font-weight: 600;
        margin-bottom: 5px;
    }
    .dc-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }
    .meta-item {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 8.5px;
        color: #64748b;
    }
    .meta-item svg { flex-shrink: 0; }
    .meta-date { color: #94a3b8; }

    .dc-header-right {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 6px;
        flex-shrink: 0;
        margin-left: 12px;
    }
    .status-pill {
        display: inline-block;
        padding: 3px 10px;
        border-radius: 20px;
        border: 1.5px solid;
        font-size: 9px;
        font-weight: 700;
        letter-spacing: 0.03em;
        white-space: nowrap;
    }
    .lots-pill {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 20px;
        background: #e0e7ff;
        color: #3730a3;
        font-size: 8.5px;
        font-weight: 600;
    }

    /* ── Lots table ─────────────────────────────── */
    .lots-wrap { padding: 10px 16px 14px; }
    .lots-title {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 10px;
        font-weight: 700;
        color: #334155;
        margin-bottom: 8px;
    }
    .lots-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        padding: 0 5px;
        border-radius: 10px;
        background: #1e40af;
        color: #fff;
        font-size: 8px;
        font-weight: 700;
    }
    .lots-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 8.5px;
    }
    .lots-table thead tr {
        background: #1e40af;
        color: #fff;
    }
    .lots-table th {
        padding: 5px 8px;
        font-weight: 600;
        text-align: left;
        font-size: 8px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
    }
    .th-center { text-align: center; }
    .th-right  { text-align: right; }
    .lots-table tbody tr { border-bottom: 1px solid #f1f5f9; }
    .lots-table tbody tr:nth-child(even) { background: #f8fafc; }
    .lots-table td { padding: 5px 8px; vertical-align: middle; }
    .td-center  { text-align: center; }
    .td-right   { text-align: right; }
    .td-price   { font-weight: 700; color: #0f172a; font-size: 9px; }
    .td-lot-name { font-weight: 600; color: #1e293b; }
    .td-extras  { color: #64748b; font-size: 7.5px; }
    .ls-badge {
        display: inline-block;
        padding: 1px 6px;
        border-radius: 10px;
        border: 1px solid;
        font-size: 7.5px;
        font-weight: 600;
        white-space: nowrap;
    }

    /* ── Footer ─────────────────────────────────── */
    .pdf-footer {
        margin-top: 24px;
        padding-top: 10px;
        border-top: 1px solid #e2e8f0;
        display: flex;
        justify-content: space-between;
        font-size: 8px;
        color: #94a3b8;
    }
    .pdf-footer-brand { font-weight: 600; color: #64748b; }
</style>
</head>
<body>

<!-- ── Header ── -->
<div class="pdf-header">
    <div class="pdf-header-left">
        <div class="pdf-title">Rapport des Dénonces</div>
        <div class="pdf-subtitle">${this.denounces.length} dénonce(s) · ${this.totalLots} lot(s) · ${date}</div>
    </div>
    <div class="pdf-header-right">
        <strong>${userName}</strong>
        ${userCompany ? `<span>${userCompany}</span>` : ''}
        ${userPhone   ? `<span>${userPhone}</span>`   : ''}
        <span class="pdf-date">Généré le ${date}</span>
    </div>
</div>

<!-- ── Summary ── -->
<div class="summary-bar">
    <div class="summary-item">
        <span class="summary-val">${this.denounces.length}</span>
        <span class="summary-lbl">Dénonces</span>
    </div>
    <div class="summary-item">
        <span class="summary-val">${this.totalLots}</span>
        <span class="summary-lbl">Lots</span>
    </div>
    <div class="summary-item">
        <span class="summary-val">${this.acceptedCount}</span>
        <span class="summary-lbl">Acceptées</span>
    </div>
    <div class="summary-item">
        <span class="summary-val">${this.denounces.filter(d => d.status === 'pending').length}</span>
        <span class="summary-lbl">En attente</span>
    </div>
    <div class="summary-item">
        <span class="summary-val">${this.denounces.filter(d => d.status === 'refused').length}</span>
        <span class="summary-lbl">Refusées</span>
    </div>
</div>

<!-- ── Denounces ── -->
${denouncesHtml}

<!-- ── Footer ── -->
<div class="pdf-footer">
    <span class="pdf-footer-brand">Plugimmo</span>
    <span>Rapport confidentiel · ${date}</span>
</div>

</body>
</html>`;
    }
}
